import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SingleSelectFilter } from '@/components/ui/single-select-filter';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, api } from '@/lib/api';
import type {
  LlmEndpoint,
  ResponsePromptVariant,
  RunResponsePromptTestResponse,
} from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  /** 현재 편집 중인 본문. */
  content: string;
  /** 부모 "변수 기본값" 의 현재 값 — single source. */
  variableValues: Record<string, string>;
  /**
   * 같은 prompt 의 모든 variant. endpoint.model 매칭 시 어떤 variant 가 사용될지 표시.
   * 변경 안 했어도 어떤 variant 본문을 편집 중인지는 부모가 알고 있어서 — 여기선 매칭 hint 만.
   */
  variants?: ResponsePromptVariant[];
  /** 사용자가 현재 편집 중인 variant — audit / temperature fallback 용. */
  editingVariantId?: string;
}

/** variant 매칭 우선순위: 정확 일치 > default ('') > null. */
function matchVariantForModel(
  variants: ResponsePromptVariant[],
  model: string | undefined,
): ResponsePromptVariant | null {
  if (variants.length === 0) return null;
  if (model) {
    const exact = variants.find((v) => v.modelMatch === model);
    if (exact) return exact;
  }
  return variants.find((v) => v.modelMatch === '') ?? null;
}

export function TestRunArea({ content, variableValues, variants = [], editingVariantId }: Props) {
  const endpointsQ = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
    staleTime: 60_000,
  });

  const [endpointId, setEndpointId] = useState<string>('');
  /** 빈 문자열 = 미설정 (서버에 temperature 미전송 → variant.temperature 또는 모델 기본값). */
  const [temperatureInput, setTemperatureInput] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RunResponsePromptTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const endpoints: LlmEndpoint[] = endpointsQ.data?.endpoints ?? [];
  const selectedEndpoint = endpoints.find((ep) => ep.id === endpointId) ?? null;
  /** endpoint.model 로 매칭되는 variant — pipeline runtime 과 동일 로직. */
  const matchedVariant = useMemo(
    () => matchVariantForModel(variants, selectedEndpoint?.model),
    [variants, selectedEndpoint],
  );

  // 편집 중인 variant 가 바뀔 때 endpoint 자동 매칭 — 그 variant 의 modelMatch 와 일치하는 endpoint
  // 가 있으면 선택, 없으면 기본 엔드포인트 (빈 문자열 = "기본 엔드포인트 사용").
  // 사용자가 이후 수동으로 endpoint 를 바꾸면 그 선택 유지 (effect 는 variant 변화에만 반응).
  // biome-ignore lint/correctness/useExhaustiveDependencies: editingVariantId 변화에만 트리거.
  useEffect(() => {
    if (!editingVariantId || endpoints.length === 0) return;
    const variant = variants.find((v) => v.id === editingVariantId);
    if (!variant) return;
    if (variant.modelMatch === '') {
      setEndpointId('');
      return;
    }
    const match = endpoints.find((ep) => ep.model === variant.modelMatch);
    setEndpointId(match?.id ?? '');
  }, [editingVariantId, endpoints.length]);

  const parsedTemperature = (() => {
    const s = temperatureInput.trim();
    if (s === '') return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  })();
  const temperatureInvalid =
    temperatureInput.trim() !== '' &&
    (parsedTemperature === undefined || parsedTemperature < 0 || parsedTemperature > 2);

  /** 자동 적용 temperature 표시용 — matched variant 의 기본값 (사용자 입력 우선). */
  const fallbackTemperature: number | null = matchedVariant?.temperature ?? null;
  const effectivePreview: number | null =
    parsedTemperature !== undefined && !temperatureInvalid
      ? parsedTemperature
      : fallbackTemperature;

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    const ctl = new AbortController();
    abortRef.current = ctl;
    try {
      const r = await api.responsePromptRunTest(
        {
          content,
          variableValues,
          endpointId: endpointId || undefined,
          ...(editingVariantId ? { variantId: editingVariantId } : {}),
          ...(parsedTemperature !== undefined && !temperatureInvalid
            ? { temperature: parsedTemperature }
            : {}),
        },
        ctl.signal,
      );
      setResult(r);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        setError(null);
      } else if (e instanceof TypeError && e.message.toLowerCase().includes('abort')) {
        setError(null);
      } else {
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : '실행 실패');
      }
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
  };

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <div className="block text-xs text-foreground-muted mb-1">LLM 엔드포인트</div>
          <SingleSelectFilter
            options={endpoints.map((ep) => ({ id: ep.id, label: ep.name, hint: ep.model }))}
            value={endpointId || null}
            onChange={(id) => setEndpointId(id ?? '')}
            triggerPlaceholder="기본 엔드포인트"
            searchPlaceholder="엔드포인트 검색…"
            triggerClassName="h-9 text-sm"
          />
        </div>
        <div className="w-28">
          <div className="block text-xs text-foreground-muted mb-1">Temperature</div>
          <Input
            type="number"
            min={0}
            max={2}
            step={0.01}
            placeholder={fallbackTemperature != null ? `자동 ${fallbackTemperature}` : '기본값'}
            value={temperatureInput}
            onChange={(e) => setTemperatureInput(e.target.value)}
            aria-invalid={temperatureInvalid}
            className="h-9 text-sm"
          />
        </div>
        {loading ? (
          <Button type="button" variant="secondary" onClick={cancel} className="h-9">
            취소
          </Button>
        ) : (
          <Button
            type="button"
            onClick={run}
            disabled={!content.trim() || temperatureInvalid}
            className="h-9"
          >
            실행
          </Button>
        )}
      </div>

      {variants.length > 0 ? (
        <div className="rounded-md bg-surface-muted/50 px-3 py-1.5 text-xs text-foreground-muted">
          {selectedEndpoint
            ? matchedVariant
              ? matchedVariant.modelMatch === ''
                ? `→ ${selectedEndpoint.model} 매칭 variant 없음 → 기본 variant 사용`
                : `→ ${matchedVariant.modelMatch} variant 매칭 (정확 일치)`
              : '→ variant 없음'
            : '→ 기본 엔드포인트 사용 (default variant)'}
          {editingVariantId &&
          matchedVariant &&
          matchedVariant.id !== editingVariantId &&
          variants.find((v) => v.id === editingVariantId)?.modelMatch !==
            matchedVariant.modelMatch ? (
            <span className="ml-2 text-orange-600 dark:text-orange-400">
              ⚠ 편집 중 variant 와 다름 — 실행되는 본문은 편집창 내용
            </span>
          ) : null}
          {effectivePreview != null ? (
            <span className="ml-2">· 적용 T = {effectivePreview}</span>
          ) : null}
        </div>
      ) : null}

      <div className="text-xs text-foreground-muted">
        위 "변수 기본값" 영역에 입력된 값으로 실행됩니다. Temperature 는 비워두면 매칭된 variant 의
        기본값 → 모델 기본값 순으로 적용 (0~2).
      </div>

      {temperatureInvalid ? (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          Temperature 는 0 과 2 사이 숫자여야 합니다.
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-md bg-accent/5 px-3 py-2 text-xs text-accent">실행 중…</div>
      ) : null}

      {error ? (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {result ? (
        <div className="space-y-1">
          <div className="text-xs text-foreground-muted">
            {result.endpointName ? `${result.endpointName} · ` : ''}
            {result.model ?? ''}
            <span className="ml-1">· {result.durationMs}ms</span>
          </div>
          <Textarea readOnly value={result.output} rows={10} className="text-sm" />
        </div>
      ) : null}
    </div>
  );
}
